import { useState, useEffect } from "react";
import { Modal, Input, message, InputNumber, Select, Button } from "antd";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useUpdateMajorMutation } from "@/redux/rtk/features/major/majorApi";

export default function UpdateMajor({ open, data, onClose }) {
    const { t } = useTranslation();
    const [updateMajorMutation] = useUpdateMajorMutation();
    const [name, setName] = useState("");
    const [price, setPrice] = useState("");
    const [currency, setCurrency] = useState("USD");
    const [priceAfterScholarship, setPriceAfterScholarship] = useState("");
    const [scholarships, setScholarships] = useState([{ percentage: null, price: null }]);
    const [loading, setLoading] = useState(false);

    const currencies = [
        { label: "USD", value: "USD" },
        { label: "EUR", value: "EUR" },
        { label: "TRY", value: "TRY" },
        { label: "GBP", value: "GBP" },
    ];

    useEffect(() => {
        if (data) {
            setName(data.name);
            setPrice(data.price);
            setCurrency(data.currency || "USD");
            setPriceAfterScholarship(data.priceAfterScholarship);

            let parsedScholarships = [{ percentage: null, price: null }];
            if (data.scholarships) {
                try {
                    parsedScholarships = typeof data.scholarships === 'string' ? JSON.parse(data.scholarships) : data.scholarships;
                    if (!Array.isArray(parsedScholarships) || parsedScholarships.length === 0) {
                        parsedScholarships = [{ percentage: null, price: null }];
                    }
                } catch (e) {
                    console.error("Failed to parse scholarships", e);
                }
            }
            setScholarships(parsedScholarships);
        }
    }, [data]);

    const addScholarshipRow = () => {
        setScholarships([...scholarships, { percentage: null, price: null }]);
    };

    const removeScholarshipRow = (index) => {
        const newScholarships = scholarships.filter((_, i) => i !== index);
        setScholarships(newScholarships);
    };

    const handleScholarshipChange = (index, field, value) => {
        const newScholarships = [...scholarships];
        newScholarships[index][field] = value;
        setScholarships(newScholarships);
    };

    const handleUpdate = async () => {
        if (!name.trim()) return;
        setLoading(true);
        try {
            await updateMajorMutation({
                id: data.id,
                values: {
                    name,
                    price,
                    currency,
                    priceAfterScholarship,
                    scholarships: scholarships.filter(s => s.percentage !== null && s.price !== null)
                }
            }).unwrap();
            onClose();
        } catch (error) {
            // Error is handled by majorApi toast handler
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            title={t("university.updateMajor") || "Update Major"}
            open={open}
            onOk={handleUpdate}
            onCancel={onClose}
            confirmLoading={loading}
            okText={t("common.update") || "Update"}
        >
            <div className="mt-4">
                <p className="mb-2">{t("university.majorName") || "Major Name"}:</p>
                <Input
                    size="large"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                />
            </div>
            <div className="grid grid-cols-2 gap-4 mt-4">
                <div>
                    <p className="mb-2">{t("common.price") || "Price"}:</p>
                    <InputNumber
                        size="large"
                        className="w-full"
                        value={price}
                        onChange={(value) => setPrice(value)}
                        onKeyPress={(event) => {
                            if (!/[0-9.]/.test(event.key)) {
                                event.preventDefault();
                            }
                        }}
                    />
                </div>
                <div>
                    <p className="mb-2">{t("common.currency") || "Currency"}:</p>
                    <Select
                        size="large"
                        className="w-full"
                        value={currency}
                        onChange={setCurrency}
                        options={currencies}
                    />
                </div>
            </div>
            <div className="mt-4">
                <p className="mb-2">{t("university.priceAfterScholarship") || "Price After Scholarship"}:</p>
                <InputNumber
                    size="large"
                    className="w-full"
                    value={priceAfterScholarship}
                    onChange={(value) => setPriceAfterScholarship(value)}
                    onKeyPress={(event) => {
                        if (!/[0-9.]/.test(event.key)) {
                            event.preventDefault();
                        }
                    }}
                />
            </div>
            <div className="mt-6">
                <div className="flex justify-between items-center mb-4">
                    <p className="font-bold">{t("university.scholarships") || "Scholarships"}</p>
                    <Button type="dashed" onClick={addScholarshipRow} icon={<PlusOutlined />}>
                        {t("university.addScholarship") || "Add Scholarship"}
                    </Button>
                </div>
                {scholarships.map((s, index) => (
                    <div key={index} className="grid grid-cols-12 gap-2 mb-3 items-end">
                        <div className="col-span-5">
                            <p className="mb-1 text-xs">{t("university.scholarshipPercentage") || "Scholarship Percentage (%)"}:</p>
                            <InputNumber
                                className="w-full"
                                min={0}
                                max={100}
                                formatter={value => `${value}%`}
                                parser={value => value.replace('%', '')}
                                value={s.percentage}
                                onChange={(val) => handleScholarshipChange(index, "percentage", val)}
                                placeholder="e.g. 50"
                            />
                        </div>
                        <div className="col-span-5">
                            <p className="mb-1 text-xs">{t("university.priceAfterScholarship") || "Price After Scholarship"}:</p>
                            <InputNumber
                                className="w-full"
                                value={s.price}
                                onChange={(val) => handleScholarshipChange(index, "price", val)}
                                placeholder="Price"
                            />
                        </div>
                        <div className="col-span-2">
                            <Button
                                danger
                                type="text"
                                icon={<DeleteOutlined />}
                                onClick={() => removeScholarshipRow(index)}
                                disabled={scholarships.length === 1}
                            />
                        </div>
                    </div>
                ))}
            </div>
        </Modal>
    );
}
